<template>
  <div class="row">
    <form @submit.prevent="validate" novalidate>

      <div class="col-12 q-pb-md q-pl-md q-pr-md">
        <div class="pw-requirements q-pa-md">
          <p class="text-weight-bold">{{ getString('password.requirements.head') }}</p>
          <ul>
            <li :class="{valid: validateNumberChars}" v-text="getString('password.requirements.characters') + ' ('+ data.password.length +')'"></li>
            <li :class="{valid: validateNumberDigits}" v-text="getString('password.requirements.digits')"></li>
            <li :class="{valid: validateNumberCaps}" v-text="getString('password.requirements.capitals')"></li>
              <li :class="{valid: validateNumberLower}" v-text="$store.state.stringsStore.language === 'nl' ? 'Minimaal 2 kleine letters' : 'At least 2 lower-case'"></li>
            <li :class="{valid: validateNumberSpecialChars}" v-text="getString('password.requirements.specialchars')"></li>
            <li>! ? $ % ^ &amp; * ( ) _ \ - + = { } [ ] : ; @ ' ~ # | &lt; , &gt; . /</li>
          </ul>
        </div>
      </div>

      <div class="col-12 q-pa-md">
        <p>{{ getString('password.new') }}</p>
        <q-field>
          <q-input
            color="white"
            type="password"
            :placeholder="getString('password.new')"
            inverted-light
            v-model="data.password"
            class="no-shadow bg-grey-11"
            @input="errorOpen = false"
          />
        </q-field>
      </div>

      <div class="col-12 q-pa-md">
        <p>{{ getString('password.retype') }}</p>
        <q-field>
          <q-input
            color="white"
            type="password"
            :placeholder="getString('password.retype')"
            inverted-light
            v-model="data.repeatPassword"
            class="no-shadow bg-grey-11"
            @input="errorOpen = false"
          />
        </q-field>
      </div>

      <div class="no-padding message" :class="{ active: errorOpen }">
        <div class="row">
          <div class="col-12 q-pa-md">
            <div class="message-inner">
              <span class="text-weight-bold message-title">
                {{ getString('login.input.error.head') }}
              </span>
              <p class="message-body">
                {{ getString('login.input.error.message') }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="no-padding message success" :class="{ active: successOpen }">
        <div class="row">
          <div class="col-12 q-pa-md">
            <div class="message-inner">
              <span class="text-weight-bold message-title">
                {{ getString('reset.success.head') }}
              </span>
              <p class="message-body">
                {{ getString('reset.success.message') }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 q-pt-md">
          <q-btn
            align="right"
            color="primary"
            :label="getString('reset.button')"
            size="md"
            type="submit"
            class="float-right capitalize"
          />
      </div>
    </form>
  </div>
</template>


<script>
import qs from 'qs';

import { helpers, required, sameAs } from 'vuelidate/lib/validators';
import getString from '@/helpers/GetStrings';

// Define custom validators
const pwRequirements = helpers.regex('pwRequirements', /^(?=.*[A-Z].*[A-Z])(?=.*[a-z].*[a-z])(?=.*[^\w\s].*[^\w\s])(?=.*\d.*\d)(?=(^.{10,}$))/);

export default {
  name: 'Reset',
  mounted() {
    this.email = decodeURIComponent(this.$route.params.email);
    this.code = this.$route.params.code;
    // Check values in url if invitation is (still) valid
    this.checkResetLink();
    // Show loader
    this.$q.loading.show();
  },
  data() {
    return {
      email: '',
      code: '',

      data: {
        password: '',
        repeatPassword: '',
      },

      errorOpen: false,
      successOpen: false,
    };
  },
  validations: {
    data: {
      password: {
        required,
        pwRequirements,
      },
      repeatPassword: {
        required,
        sameAs: sameAs('password'),
      },
    },
  },
  methods: {
    getString,
    async checkResetLink() {
      try {
        const response = await this.axios.get(`reset/check?code=${this.code}&email=${this.email}`);
        if (response) {
          this.$q.loading.hide();
        }
      } catch (error) {
        const self = this;
        setTimeout(() => {
          // Hide loader
          this.$q.loading.hide();
          this.$q.notify({
            message: getString('reset.error.invalid'),
            position: 'center',
            timeout: 2000,
            color: 'negative',
            onDismiss() {
              self.$router.push('/login');
            },
          });
        }, 500);
      }
    },
    validate() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        const data = {
          email: this.email,
          code: this.code,
          password: this.data.password,
        };

        this.axios.post('reset/save', qs.stringify(data))
          .then(() => {
            this.successOpen = true;
            this.errorOpen = false;
            setTimeout(() => {
              this.$router.push('/login');
            }, 4000);
          })
          .catch(() => {
            this.errorOpen = true;
            this.successOpen = false;
          });
      } else {
        this.errorOpen = true;
        this.successOpen = false;
      }
    },
  },
  computed: {
    // The following 4 properties are to let the user know if the entered password is valid
    validateNumberChars() {
      return (this.data.password.length >= 10);
    },
    validateNumberDigits() {
      return /(?=.*\d.*\d)/.test(this.data.password);
    },
    validateNumberCaps() {
      return /(?=.*[A-Z].*[A-Z])/.test(this.data.password);
    },
    validateNumberLower() {
      return /(?=.*[a-z].*[a-z])/.test(this.data.password);
    },
    validateNumberSpecialChars() {
      return /(?=.*[^\w\s].*[^\w\s])/.test(this.data.password);
    },
  },
};
</script>

<style lang="stylus" scoped>
@import '~variables';
.light
  color: $ctrl-grey-darken-3
  line-height 21px

.message
  width: calc(100% + 1rem);
  margin-left: -0.5rem;
  background-color: $ctrl-red;
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.5s ease;

  &.success
    background-color: $ctrl-turquoise

  &.active
    max-height: 1000px;

.message-inner
  padding-left: 1rem;
  padding-right: 1rem;

.message-title
  color: rgba(0, 0, 0, 0.55);
  text-shadow: hsla(0, 0%, 100%, 0.1) 0 1px 0;
  margin-bottom: 0.5rem;
  display: inline-block;

.message-body
  color: rgba(0, 0, 0, 0.55);
  text-shadow: hsla(0, 0%, 100%, 0.1) 0 1px 0;
  margin-bottom: 0;
</style>
